import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box, Typography, Paper, IconButton, Modal, TextField, Button, Grid, InputAdornment, Switch, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { toast, ToastContainer } from 'react-toastify'; // Import both toast and ToastContainer
import 'react-toastify/dist/ReactToastify.css'; // Import Toastify styles
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import '../styles/purohit.css';
import { authToken } from '../globals/Global';
import { addPurohit, purohitUrl, getPurohitById, updatePurohit } from '../globals/Services';

export default function PurohitManagement() {
  const [purohitRows, setPurohitRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNo: '',
    email: '',
    dob: null,
    tob: null,
    gender: '',    
    pob: '',
    country: '',
    experience: '',
    speciality: '',
    languages: '',
    description: '',
    aadharDocUrl: '',
    panDocUrl: '',
    cancelledChequeUrl: ''
  });

  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNo: '',
    email: '',
    dob: null,
    tob: null,
    gender: '',
    pob: '',
    country: '',
    experience: '',
    speciality: '',
    languages: '',
    description: '',
    aadharDocUrl: '',
    panDocUrl: '',
    cancelledChequeUrl: ''
  });

  // Gender options for dropdown
  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' }
  ];

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditField = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Format date and time for API submission
  const formatDateTime = (dateTime, isTime = false) => {
    if (!dateTime) return '';
    if (isTime) {
      return dayjs(dateTime).format('HH:mm:ss');
    }
    return dayjs(dateTime).format('YYYY-MM-DD');
  };

  // Parse date and time from API response
  const parseDateTime = (dateTimeString, isTime = false) => {
    if (!dateTimeString) return null;
    if (isTime) {
      const today = dayjs().format('YYYY-MM-DD');
      return dayjs(`${today} ${dateTimeString}`);
    }
    return dayjs(dateTimeString);
  };

  // Handle verification status toggle
  const handleVerificationToggle = async (userId, currentVerified) => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const updateUrl = updatePurohit(userId);
      
      const userRes = await axios.get(getPurohitById(userId), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const currentUserData = userRes.data?.data ?? userRes.data;
      
      const updateData = {
        ...currentUserData,
        verified: !currentVerified
      };

      console.log('Sending update data:', updateData);
      
      const response = await axios.put(updateUrl, updateData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Verification status updated:', response.data);
      
      setPurohitRows(prevRows => 
        prevRows.map(row => 
          row.userId === userId 
            ? { 
                ...row, 
                verified: !currentVerified ? 'Verified' : 'Not Verified',
                verifiedBoolean: !currentVerified
              }
            : row
        )
      );
      
      toast.success(`Purohit ${!currentVerified ? 'verified' : 'unverified'} successfully!`);
      
    } catch (error) {
      console.error('Error updating verification status:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to update verification status.');
      
      setPurohitRows(prevRows => 
        prevRows.map(row => 
          row.userId === userId 
            ? { 
                ...row, 
                verified: currentVerified ? 'Verified' : 'Not Verified',
                verifiedBoolean: currentVerified
              }
            : row
        )
      );
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = authToken || localStorage.getItem('sessionToken');

      const dataToSend = {
        ...formData,
        phoneNo: `+ 91 ${formData.phoneNo}`,
        dob: formatDateTime(formData.dob),
        tob: formatDateTime(formData.tob, true),
      };

      const response = await axios.post(addPurohit, {
        ...dataToSend,
        roles: 'PUROHIT'
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Purohit added:', response.data);
      toast.success('Purohit added successfully!');
      setOpenModal(false);
      setFormData({
        firstName: '',
        lastName: '',
        phoneNo: '',
        email: '',
        dob: null,
        tob: null,
        gender: '',
        pob: '',
        country: '',
        experience: '',
        speciality: '',
        languages: '',
        description: '',
        panDocUrl: '',
        aadharDocUrl: '',
        cancelledChequeUrl: ''  
      });
      fetchPurohitData();
    } catch (error) {
      console.error('Error adding Purohit:', error);
      toast.error('Failed to add Purohit.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      const updateUrl = updatePurohit(editData.userId || editData.id);

      let cleanPhoneNo = editFormData.phoneNo.replace(/^\+\s*91\s*/, '').trim();
      
      const dataToSend = {
        ...editFormData,
        phoneNo: `+ 91 ${cleanPhoneNo}`,
        dob: formatDateTime(editFormData.dob),
        tob: formatDateTime(editFormData.tob, true),
        roles: 'PUROHIT'
      };

      const response = await axios.put(updateUrl, dataToSend, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Purohit updated:', response.data);
      toast.success('Purohit updated successfully!');
      setOpenEditModal(false);
      setEditData(null);
      setEditFormData({
        firstName: '',
        lastName: '',
        phoneNo: '',
        email: '',
        dob: null,
        tob: null,
        gender: '',
        pob: '',
        country: '',
        experience: '',
        speciality: '',
        languages: '',
        description: '',
        panDocUrl: '',
        aadharDocUrl: '',
        cancelledChequeUrl: ''
      });
      fetchPurohitData();
    } catch (error) {
      console.error('Error updating Purohit:', error);
      toast.error('Failed to update Purohit.');
    }
  };

  const handleEdit = async (row) => {
    try {
      const token = authToken || localStorage.getItem('sessionToken');
      
      const userRes = await axios.get(getPurohitById(row.userId || row.id), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const userData = userRes.data?.data ?? userRes.data;
      setEditData(userData);
      
      let displayPhoneNo = userData.phoneNo || '';
      displayPhoneNo = displayPhoneNo.replace(/^\+\s*91\s*/, '').trim();
      
      setEditFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phoneNo: displayPhoneNo,
        email: userData.email || '',
        dob: parseDateTime(userData.dob),
        tob: parseDateTime(userData.tob, true),
        gender: userData.gender || '',
        pob: userData.pob || '',
        country: userData.country || '',
        experience: userData.experience || '',
        speciality: userData.speciality || '',
        languages: userData.languages || '',
        description: userData.description || '',
        aadharDocUrl: userData.aadharDocUrl || '',
        panDocUrl: userData.panDocUrl || '',
        cancelledChequeUrl: userData.cancelledChequeUrl || ''
      });
      
      setOpenEditModal(true);
    } catch (error) {
      console.error('Error fetching purohit data for edit:', error);
      toast.error('Failed to load purohit data for editing.');
    }
  };

  const fetchPurohitData = async () => {
  try {
    setLoading(true);
    const token = authToken || localStorage.getItem('sessionToken');

    const response = await axios.get(purohitUrl, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    const purohitProfiles = response.data?.dataList || [];

    const mapped = purohitProfiles.map((purohit, index) => ({
      id: purohit.purohitId || index,
      userId: purohit.userId,

      fullName: `${purohit.firstName || ''} ${purohit.lastName || ''}`.trim(),
      mobile: purohit.phoneNo || 'N/A',
      email: purohit.email || 'N/A',

      verified: purohit.verified === true ? 'Verified' : 'Not Verified',
      verifiedBoolean: purohit.verified === true,

      status: purohit.status || 'Inactive',

      createdAtRaw: purohit.createdAt
        ? dayjs(purohit.createdAt).toDate()
        : null,

      createdAt: purohit.createdAt
        ? dayjs(purohit.createdAt).format('DD-MM-YYYY')
        : 'N/A',
    }));

    setPurohitRows(mapped);

  } catch (error) {
    console.error('Error loading purohit data:', error);
    setPurohitRows([]);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchPurohitData();
  }, []);

  const columns = [
    { field: 'id', headerName: 'ID', width: 80, headerAlign: 'center', align: 'center', renderHeader: () => <strong>ID</strong> },
    { field: 'fullName', headerName: 'Full Name', headerAlign: 'center', align: 'center', flex: 1, renderHeader: () => <strong>Full Name</strong> },
    { field: 'mobile', headerName: 'Mobile', headerAlign: 'center', align: 'center', flex: 1, renderHeader: () => <strong>Mobile</strong> },
    { field: 'email', headerName: 'Email ID', headerAlign: 'center', align: 'center', flex: 1.5, renderHeader: () => <strong>Email ID</strong> },
    {
      field: 'verified',
      headerName: 'Verified',
      flex: 1.2,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <strong>Verified</strong>,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <FormControlLabel
            control={
              <Switch
                checked={params.row.verifiedBoolean}
                onChange={() => handleVerificationToggle(params.row.userId, params.row.verifiedBoolean)}
                color="primary"
                size="small"
              />
            }
            label={params.row.verifiedBoolean ? 'Verified' : 'Not Verified'}
            labelPlacement="start"
            sx={{ 
              margin: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '12px',
                color: params.row.verifiedBoolean ? 'green' : 'red',
                fontWeight: 'bold'
              }
            }}
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <strong>Status</strong>,
      renderCell: (params) => {
        const statusFormatted = params.value.toLowerCase().charAt(0).toUpperCase() + params.value.slice(1).toLowerCase();
        return (
          <Typography
            sx={{
              color: params.value === 'ACTIVE' ? 'green' : (params.value === 'INACTIVE' ? 'red' : 'grey'),
              fontWeight: 'bold',
              fontSize: '14px',
              textAlign: 'center',
              justifyContent: 'center',
              margin: '15px 0px 0px 0px',
            }}
          >
            {statusFormatted}
          </Typography>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      type: 'date',                          // ← tells DataGrid it's a date
      valueGetter: (value, row) => row.createdAtRaw,  // ← use raw Date for logic
      renderCell: (params) => params.row.createdAt,   // ← show formatted string
      renderHeader: () => <strong>Date</strong>,
    },
    {
      field: 'Edit',
      headerName: 'Edit',
      width: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderHeader: () => <strong>Edit</strong>,
      renderCell: (params) => (
        <IconButton 
          onClick={() => handleEdit(params.row)} 
          size="small"
          sx={{ color: '#1976d2' }}
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="purohit-table-container">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="600">Purohit Management</Typography>
          <button className="purohit-button" onClick={() => setOpenModal(true)}>+ Add Purohit</button>
        </Box>

        <Paper elevation={2}>
          <Box className="purohit-table-wrapper">
            <div style={{ width: '100%', minWidth: '800px', height: 430 }}>
              <DataGrid
                rows={purohitRows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                  sorting: {
                    sortModel: [{ field: 'id', sort: 'asc' }],
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                disableColumnFilter={false}
                disableColumnMenu={false}
                loading={loading}
              />
            </div>
          </Box>
        </Paper>

        {/* Add Modal Form */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 850,
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Add Purohit</Typography>
              <Button variant="text" onClick={() => setOpenModal(false)} sx={{ fontSize: '18px' }}>
                ✖
              </Button>
            </Box>

            <form onSubmit={handleFormSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}><TextField label="First Name" fullWidth value={formData.firstName} onChange={(e) => updateField('firstName', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Last Name" fullWidth value={formData.lastName} onChange={(e) => updateField('lastName', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Phone No"
                    fullWidth
                    value={formData.phoneNo}
                    onChange={(e) => updateField('phoneNo', e.target.value)}
                    type="tel"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                    }}
                    sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}><TextField label="Email" fullWidth value={formData.email} onChange={(e) => updateField('email', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>

                <Grid item xs={12} sm={3}>
                  <DatePicker
                    label="Date of Birth"
                    value={formData.dob}
                    onChange={(newValue) => updateField('dob', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                    sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TimePicker
                    label="Time of Birth"
                    value={formData.tob}
                    onChange={(newValue) => updateField('tob', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                    sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender}
                      label="Gender"
                      onChange={(e) => updateField('gender', e.target.value)}
                      sx={{ width: '245px' }}
                    >
                      {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}><TextField label="Place of Birth" fullWidth value={formData.pob} onChange={(e) => updateField('pob', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Country" fullWidth value={formData.country} onChange={(e) => updateField('country', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Experience" fullWidth value={formData.experience} onChange={(e) => updateField('experience', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Speciality" fullWidth value={formData.speciality} onChange={(e) => updateField('speciality', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Languages" fullWidth value={formData.languages} onChange={(e) => updateField('languages', e.target.value)} 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={1}
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    sx={{
                    width: '766px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  />
                </Grid>

                <Grid item xs={12}><TextField label="PAN Card" fullWidth type="url" value={formData.panDocUrl} onChange={(e) => updateField('panDocUrl', e.target.value)} placeholder="https://example.com/pan.pdf" 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12}><TextField label="Aadhar Card" fullWidth type="url" value={formData.aadharDocUrl} onChange={(e) => updateField('aadharDocUrl', e.target.value)} placeholder="https://example.com/aadhar.pdf" 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
                <Grid item xs={12}><TextField label="Cancelled Cheque" fullWidth type="url" value={formData.cancelledChequeUrl} onChange={(e) => updateField('cancelledChequeUrl', e.target.value)} placeholder="https://example.com/cheque.pdf" 
                  sx={{
                    width: '245px',
                    height: '56px',
                    minHeight: '56px',                    
                  }}
                  /></Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => setOpenModal(false)}>Cancel</Button>
                <Button type="submit" variant="contained">Submit</Button>
              </Box>
            </form>
          </Box>
        </Modal>

        {/* Edit Modal Form */}
        <Modal open={openEditModal} onClose={() => setOpenEditModal(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1000,
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Edit Purohit</Typography>
              <Button variant="text" onClick={() => setOpenEditModal(false)} sx={{ fontSize: '18px' }}>✖</Button>
            </Box>

            <form onSubmit={handleEditSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}><TextField label="First Name" fullWidth value={editFormData.firstName} onChange={(e) => updateEditField('firstName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Last Name" fullWidth value={editFormData.lastName} onChange={(e) => updateEditField('lastName', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Phone No"
                    fullWidth
                    value={editFormData.phoneNo}
                    onChange={(e) => updateEditField('phoneNo', e.target.value)}
                    type="tel"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}><TextField label="Email" fullWidth value={editFormData.email} onChange={(e) => updateEditField('email', e.target.value)} /></Grid>

                <Grid item xs={12} sm={3}>
                  <DatePicker
                    label="Date of Birth"
                    value={editFormData.dob}
                    onChange={(newValue) => updateEditField('dob', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <TimePicker
                    label="Time of Birth"
                    value={editFormData.tob}
                    onChange={(newValue) => updateEditField('tob', newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={editFormData.gender}
                      label="Gender"
                      onChange={(e) => updateEditField('gender', e.target.value)}
                    >
                      {genderOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3}><TextField label="Place of Birth" fullWidth value={editFormData.pob} onChange={(e) => updateEditField('pob', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Country" fullWidth value={editFormData.country} onChange={(e) => updateEditField('country', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Experience" fullWidth value={editFormData.experience} onChange={(e) => updateEditField('experience', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Speciality" fullWidth value={editFormData.speciality} onChange={(e) => updateEditField('speciality', e.target.value)} /></Grid>
                <Grid item xs={12} sm={3}><TextField label="Languages" fullWidth value={editFormData.languages} onChange={(e) => updateEditField('languages', e.target.value)} /></Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={1}
                    value={editFormData.description}
                    onChange={(e) => updateEditField('description', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12}><TextField label="PAN Card" fullWidth type="url" value={editFormData.panDocUrl} onChange={(e) => updateEditField('panDocUrl', e.target.value)} placeholder="https://example.com/pan.pdf" /></Grid>
                <Grid item xs={12}><TextField label="Aadhar Card" fullWidth type="url" value={editFormData.aadharDocUrl} onChange={(e) => updateEditField('aadharDocUrl', e.target.value)} placeholder="https://example.com/aadhar.pdf" /></Grid>
                <Grid item xs={12}><TextField label="Cancelled Cheque" fullWidth type="url" value={editFormData.cancelledChequeUrl} onChange={(e) => updateEditField('cancelledChequeUrl', e.target.value)} placeholder="https://example.com/cheque.pdf" /></Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={() => setOpenEditModal(false)}>Cancel</Button>
                <Button type="submit" variant="contained">Update</Button>
              </Box>
            </form>
          </Box>
        </Modal>

        {/* ToastContainer - Place at the end of the main component */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Box>
    </LocalizationProvider>
  );
}